import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { ComplianceService } from "@/gen/omnios/v1/compliance_connect";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { cn } from "@/lib/utils";
import { useWorkspaceStore } from "@/lib/workspace";
import { createClient } from "@connectrpc/connect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import {
	AlertTriangle,
	CheckCircle,
	FileText,
	Loader2,
	Plus,
	Trash2,
	Upload,
} from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/settings/compliance")({
	beforeLoad: () => {
		const ws = useWorkspaceStore.getState().selected();
		if (!ws?.features.includes("compliance")) {
			throw redirect({ to: "/settings" });
		}
	},
	component: ComplianceSettingsPage,
});

const client = createClient(ComplianceService, transport);

type TabId = "cert-types" | "alerts" | "import";

function ComplianceSettingsPage() {
	const { t } = useTranslation("settings");

	const TABS = [
		{ id: "cert-types" as const, label: t("compliance_tab_cert_types") },
		{ id: "alerts" as const, label: t("compliance_tab_alerts") },
		{ id: "import" as const, label: t("compliance_tab_import") },
	];

	useBreadcrumbs([
		{ label: t("breadcrumb_settings"), href: "/settings" },
		{ label: t("compliance_breadcrumb") },
	]);

	const [activeTab, setActiveTab] = useState<TabId>("cert-types");

	return (
		<div>
			<div className="-mx-6 -mt-4 flex border-b bg-muted/30">
				{TABS.map((tab) => (
					<Button
						key={tab.id}
						variant="ghost"
						size="sm"
						onClick={() => setActiveTab(tab.id)}
						className={cn(
							"px-5 py-2.5 rounded-none border-b-2 -mb-px",
							activeTab === tab.id
								? "border-primary text-foreground bg-background"
								: "border-transparent text-muted-foreground hover:text-foreground",
						)}
					>
						{tab.label}
					</Button>
				))}
			</div>

			<div className="pt-5">
				{activeTab === "cert-types" && <CertificationTypesTab />}
				{activeTab === "alerts" && <AlertsTab />}
				{activeTab === "import" && <ImportTab />}
			</div>
		</div>
	);
}

function CertificationTypesTab() {
	const { t } = useTranslation("settings");
	const qc = useQueryClient();
	const [isCreating, setIsCreating] = useState(false);
	const [formData, setFormData] = useState({
		code: "",
		name: "",
		description: "",
		typicalValidityMonths: "12",
		requiresRenewal: true,
		badgeColor: "#10B981",
		icon: "file-text",
	});

	const { data: typesResp, isLoading } = useQuery({
		queryKey: ["certification-types"],
		queryFn: () => client.listCertificationTypes({}),
	});

	const types = typesResp?.types ?? [];

	const initDefaultsMut = useMutation({
		mutationFn: () => client.initDefaultCertificationTypes({}),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["certification-types"] });
		},
	});

	const createMut = useMutation({
		mutationFn: () =>
			client.createCertificationType({
				code: formData.code,
				name: formData.name,
				description: formData.description,
				issuingBodies: [],
				typicalValidityMonths:
					Number.parseInt(formData.typicalValidityMonths) || 12,
				requiresRenewal: formData.requiresRenewal,
				badgeColor: formData.badgeColor,
				icon: formData.icon,
				sortOrder: types.length,
			}),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["certification-types"] });
			setIsCreating(false);
			resetForm();
		},
	});

	const deleteMut = useMutation({
		mutationFn: (id: string) => client.deleteCertificationType({ id }),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["certification-types"] });
		},
	});

	const resetForm = () => {
		setFormData({
			code: "",
			name: "",
			description: "",
			typicalValidityMonths: "12",
			requiresRenewal: true,
			badgeColor: "#10B981",
			icon: "file-text",
		});
	};

	if (isLoading) {
		return (
			<Card className="p-6">
				<Skeleton className="h-6 w-48 mb-4" />
				<Skeleton className="h-24 w-full" />
			</Card>
		);
	}

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between">
				<p className="text-sm text-muted-foreground">
					{t("compliance_cert_desc")}
				</p>
				<div className="flex items-center gap-2">
					{types.length === 0 && (
						<Button
							variant="outline"
							size="sm"
							onClick={() => initDefaultsMut.mutate()}
							disabled={initDefaultsMut.isPending}
						>
							{initDefaultsMut.isPending && (
								<Loader2 className="h-4 w-4 mr-2 animate-spin" />
							)}
							{t("compliance_init_defaults")}
						</Button>
					)}
					<Button size="sm" onClick={() => setIsCreating(true)}>
						<Plus className="h-4 w-4 mr-2" />
						{t("compliance_add_type")}
					</Button>
				</div>
			</div>

			{types.length === 0 ? (
				<Card className="p-8 text-center">
					<FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
					<h3 className="text-lg font-medium mb-1">
						{t("compliance_no_types_title")}
					</h3>
					<p className="text-sm text-muted-foreground mb-4">
						{t("compliance_no_types_desc")}
					</p>
				</Card>
			) : (
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
					{types.map((type) => (
						<Card key={type.id} className="p-4">
							<div className="flex items-start justify-between">
								<div>
									<div className="flex items-center gap-2 mb-1">
										<Badge
											style={{ backgroundColor: type.badgeColor || "#10B981" }}
											className="text-white text-xs"
										>
											{type.code}
										</Badge>
										{type.requiresRenewal && (
											<span className="text-xs text-muted-foreground">
												{t("compliance_requires_renewal")}
											</span>
										)}
									</div>
									<h3 className="font-medium">{type.name}</h3>
									{type.description && (
										<p className="text-sm text-muted-foreground mt-1">
											{type.description}
										</p>
									)}
									{type.typicalValidityMonths > 0 && (
										<p className="text-xs text-muted-foreground mt-2">
											{t("compliance_validity_months", {
												months: type.typicalValidityMonths,
											})}
										</p>
									)}
								</div>
								<Button
									variant="ghost"
									size="sm"
									className="text-muted-foreground hover:text-destructive"
									onClick={() => deleteMut.mutate(type.id)}
									disabled={deleteMut.isPending}
								>
									<Trash2 className="h-4 w-4" />
								</Button>
							</div>
						</Card>
					))}
				</div>
			)}

			{/* Create dialog */}
			<Dialog open={isCreating} onOpenChange={setIsCreating}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t("compliance_add_type_title")}</DialogTitle>
					</DialogHeader>
					<div className="space-y-4">
						<div className="grid grid-cols-2 gap-4">
							<div className="space-y-2">
								<Label>{t("compliance_label_code")}</Label>
								<Input
									value={formData.code}
									onChange={(e) =>
										setFormData((d) => ({ ...d, code: e.target.value }))
									}
									placeholder={t("compliance_code_placeholder")}
								/>
							</div>
							<div className="space-y-2">
								<Label>{t("compliance_label_cert_name")}</Label>
								<Input
									value={formData.name}
									onChange={(e) =>
										setFormData((d) => ({ ...d, name: e.target.value }))
									}
									placeholder={t("compliance_cert_name_placeholder")}
								/>
							</div>
						</div>
						<div className="space-y-2">
							<Label>{t("common:description")}</Label>
							<Input
								value={formData.description}
								onChange={(e) =>
									setFormData((d) => ({ ...d, description: e.target.value }))
								}
								placeholder={t("compliance_desc_placeholder")}
							/>
						</div>
						<div className="grid grid-cols-2 gap-4">
							<div className="space-y-2">
								<Label>{t("compliance_label_validity")}</Label>
								<Input
									type="number"
									value={formData.typicalValidityMonths}
									onChange={(e) =>
										setFormData((d) => ({
											...d,
											typicalValidityMonths: e.target.value,
										}))
									}
								/>
							</div>
							<div className="space-y-2">
								<Label>{t("compliance_label_color")}</Label>
								<Input
									type="color"
									value={formData.badgeColor}
									onChange={(e) =>
										setFormData((d) => ({ ...d, badgeColor: e.target.value }))
									}
									className="h-10"
								/>
							</div>
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setIsCreating(false)}>
							{t("common:cancel")}
						</Button>
						<Button
							onClick={() => createMut.mutate()}
							disabled={!formData.code || !formData.name || createMut.isPending}
						>
							{t("common:create")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}

function AlertsTab() {
	const { t } = useTranslation("settings");
	const { data: alertsResp, isLoading } = useQuery({
		queryKey: ["expiring-certifications"],
		queryFn: () => client.getExpiringCertifications({ daysAhead: 90 }),
	});

	const alerts = alertsResp?.certifications ?? [];
	const expiringCount = alerts.filter((c) => c.daysUntilExpiry > 0).length;
	const expiredCount = alerts.filter((c) => c.daysUntilExpiry <= 0).length;

	if (isLoading) {
		return (
			<Card className="p-6">
				<Skeleton className="h-6 w-48 mb-4" />
				<Skeleton className="h-24 w-full" />
			</Card>
		);
	}

	return (
		<div className="space-y-4">
			<p className="text-sm text-muted-foreground">
				{t("compliance_alerts_desc")}
			</p>

			<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
				<Card className="p-4">
					<div className="flex items-center gap-3">
						<div className="h-10 w-10 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
							<AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
						</div>
						<div>
							<p className="text-2xl font-semibold">{expiringCount}</p>
							<p className="text-sm text-muted-foreground">
								{t("compliance_expiring")}
							</p>
						</div>
					</div>
				</Card>
				<Card className="p-4">
					<div className="flex items-center gap-3">
						<div className="h-10 w-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
							<AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
						</div>
						<div>
							<p className="text-2xl font-semibold">{expiredCount}</p>
							<p className="text-sm text-muted-foreground">
								{t("compliance_overdue")}
							</p>
						</div>
					</div>
				</Card>
				<Card className="p-4">
					<div className="flex items-center gap-3">
						<div className="h-10 w-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
							<CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
						</div>
						<div>
							<p className="text-2xl font-semibold">
								{alerts.length - expiringCount - expiredCount}
							</p>
							<p className="text-sm text-muted-foreground">
								{t("compliance_ok")}
							</p>
						</div>
					</div>
				</Card>
			</div>

			{alerts.length === 0 ? (
				<Card className="p-8 text-center">
					<CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500/50" />
					<h3 className="text-lg font-medium mb-1">
						{t("compliance_all_ok_title")}
					</h3>
					<p className="text-sm text-muted-foreground">
						{t("compliance_all_ok_desc")}
					</p>
				</Card>
			) : (
				<Card>
					<div className="p-4 border-b">
						<h3 className="font-medium">{t("compliance_need_attention")}</h3>
					</div>
					<div className="divide-y">
						{alerts.map((cert) => (
							<div
								key={cert.certificationId}
								className="p-4 flex items-center justify-between"
							>
								<div>
									<p className="font-medium">{cert.certificateNumber}</p>
									<p className="text-sm text-muted-foreground">
										{cert.productTitle} — {cert.certificationTypeName}
									</p>
								</div>
								<Badge
									className={cn(
										"text-xs",
										cert.daysUntilExpiry <= 0
											? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
											: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
									)}
								>
									{cert.daysUntilExpiry <= 0
										? t("compliance_overdue_badge")
										: t("compliance_days_badge", {
												days: cert.daysUntilExpiry,
											})}
								</Badge>
							</div>
						))}
					</div>
				</Card>
			)}
		</div>
	);
}

function ImportTab() {
	const { t } = useTranslation("settings");
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [importType, setImportType] = useState<"okpd2" | "tnved" | "gost">(
		"okpd2",
	);

	const importOkpd2Mut = useMutation({
		mutationFn: (
			codes: { code: string; name: string; parentCode?: string }[],
		) => client.bulkImportOkpd2({ codes }),
	});

	const importTnVedMut = useMutation({
		mutationFn: (codes: { code: string; name: string }[]) =>
			client.bulkImportTnVed({ codes }),
	});

	const importGostMut = useMutation({
		mutationFn: (
			standards: {
				code: string;
				title: string;
				standardType: string;
				status: string;
			}[],
		) => client.bulkImportGostStandards({ standards }),
	});

	const handleFileUpload = async (file: File) => {
		// Parse CSV/JSON file
		const text = await file.text();
		try {
			const data = JSON.parse(text);
			if (importType === "okpd2") {
				importOkpd2Mut.mutate(data);
			} else if (importType === "tnved") {
				importTnVedMut.mutate(data);
			} else {
				importGostMut.mutate(data);
			}
		} catch {
			alert(t("compliance_import_parse_error"));
		}
	};

	const isLoading =
		importOkpd2Mut.isPending ||
		importTnVedMut.isPending ||
		importGostMut.isPending;
	const lastResult =
		importOkpd2Mut.data || importTnVedMut.data || importGostMut.data;

	return (
		<div className="space-y-4">
			<p className="text-sm text-muted-foreground">
				{t("compliance_import_desc")}
			</p>

			<Card className="p-6">
				<div className="space-y-4">
					<div className="space-y-2">
						<Label>{t("compliance_import_data_type")}</Label>
						<Select
							value={importType}
							onValueChange={(v) => setImportType(v as typeof importType)}
						>
							<SelectTrigger className="w-full md:w-64">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="okpd2">
									{t("compliance_import_okpd2")}
								</SelectItem>
								<SelectItem value="tnved">
									{t("compliance_import_tnved")}
								</SelectItem>
								<SelectItem value="gost">
									{t("compliance_import_gost")}
								</SelectItem>
							</SelectContent>
						</Select>
					</div>

					<div className="space-y-2">
						<Label>{t("compliance_import_file_label")}</Label>
						<div
							className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
							onClick={() => fileInputRef.current?.click()}
						>
							<Upload className="h-10 w-10 mx-auto mb-3 text-muted-foreground/50" />
							<p className="text-sm font-medium">
								{t("compliance_import_click")}
							</p>
							<p className="text-xs text-muted-foreground mt-1">
								{t("compliance_import_format_hint")}
							</p>
						</div>
						<input
							ref={fileInputRef}
							type="file"
							accept=".json"
							className="hidden"
							onChange={(e) => {
								const file = e.target.files?.[0];
								if (file) handleFileUpload(file);
								e.target.value = "";
							}}
						/>
					</div>

					{isLoading && (
						<div className="flex items-center gap-2 text-sm text-muted-foreground">
							<Loader2 className="h-4 w-4 animate-spin" />
							{t("compliance_importing")}
						</div>
					)}

					{lastResult && (
						<div className="p-3 bg-muted/50 rounded-md text-sm">
							<p>
								{t("compliance_import_result", {
									imported: lastResult.imported,
									updated: lastResult.updated,
								})}
								{lastResult.errors > 0 && (
									<span className="text-destructive">
										{t("compliance_import_errors", {
											errors: lastResult.errors,
										})}
									</span>
								)}
							</p>
						</div>
					)}
				</div>
			</Card>

			<Card className="p-6">
				<h3 className="font-medium mb-4">{t("compliance_format_title")}</h3>
				<div className="space-y-4 text-sm">
					<div>
						<p className="font-medium text-muted-foreground mb-1">
							{t("compliance_format_okpd2_label")}:
						</p>
						<pre className="bg-muted p-2 rounded text-xs overflow-x-auto">
							{t("compliance_format_okpd2_example")}
						</pre>
					</div>
					<div>
						<p className="font-medium text-muted-foreground mb-1">
							{t("compliance_format_tnved_label")}:
						</p>
						<pre className="bg-muted p-2 rounded text-xs overflow-x-auto">
							{t("compliance_format_tnved_example")}
						</pre>
					</div>
					<div>
						<p className="font-medium text-muted-foreground mb-1">
							{t("compliance_format_gost_label")}:
						</p>
						<pre className="bg-muted p-2 rounded text-xs overflow-x-auto">
							{t("compliance_format_gost_example")}
						</pre>
					</div>
				</div>
			</Card>
		</div>
	);
}
